import { ethers } from "ethers";

// Polygon (and its Amoy testnet) enforce a network-wide MINIMUM priority fee
// of 25 Gwei: a transaction whose tip is below that floor is rejected outright
// with "transaction gas price below minimum". Wallet-signed writes that pass no
// gas fields let MetaMask pick a ~1.5 Gwei tip and get rejected before mining.
// This helper produces EIP-1559 gas fields that clear the floor for
// Polygon-family chains, and produces NOTHING for local Hardhat (chain 31337)
// or any unknown chain, which use legacy gas with no minimum — setting 1559
// fields there would break local runs. Mirrors the backend's
// web3/gasOverrides.js floor/cap semantics so server- and wallet-signed writes
// bid the same way.

// Chain ids that enforce the Polygon priority-fee floor.
const POLYGON_FAMILY_CHAIN_IDS = new Set([137, 80002]);

// The tip we bid, in Gwei. Sits comfortably above Polygon's 25 Gwei minimum so
// brief network bumps don't drop us under the floor. Overridable per-build via
// NEXT_PUBLIC_MIN_PRIORITY_FEE_GWEI.
const priorityFeeGwei = () => {
  const raw = parseFloat(process.env.NEXT_PUBLIC_MIN_PRIORITY_FEE_GWEI);
  return Number.isFinite(raw) && raw > 0 ? raw : 30;
};

// Fallback maxFeePerGas ceiling (Gwei) used only when the live base fee can't
// be read. Generous enough to cover normal Amoy/Polygon base fees on top of the
// tip. Overridable via NEXT_PUBLIC_MAX_FEE_PER_GAS_GWEI.
const fallbackMaxFeeGwei = () => {
  const raw = parseFloat(process.env.NEXT_PUBLIC_MAX_FEE_PER_GAS_GWEI);
  return Number.isFinite(raw) && raw > 0 ? raw : 200;
};

// Reads the latest base fee so maxFeePerGas can be sized to actual network
// conditions. Tries getFeeData first (single call) then falls back to the
// latest block. Returns a bigint or null when unavailable.
const readBaseFee = async (provider) => {
  try {
    const feeData = await provider.getFeeData();
    // ethers v6 getFeeData exposes maxFeePerGas/maxPriorityFeePerGas, not the
    // raw base fee, so derive base = maxFee - 2*priorityTip when present.
    if (feeData) {
      if (feeData.maxFeePerGas != null && feeData.maxPriorityFeePerGas != null) {
        const derived = feeData.maxFeePerGas - feeData.maxPriorityFeePerGas;
        if (derived > 0n) return derived;
      }
      if (feeData.gasPrice != null && feeData.gasPrice > 0n) {
        return feeData.gasPrice;
      }
    }
  } catch (_) {
    // fall through to block read
  }
  try {
    const block = await provider.getBlock("latest");
    if (block && block.baseFeePerGas) return block.baseFeePerGas;
  } catch (_) {
    // fall through to null (caller uses the ceiling fallback)
  }
  return null;
};

// Resolve the chain id: prefer a caller-supplied id (avoids a round-trip when
// the app already tracks networkId), else ask the provider. Never throws.
const resolveChainId = async (provider, knownChainId) => {
  if (Number.isFinite(knownChainId)) return Number(knownChainId);
  try {
    const network = await provider.getNetwork();
    return network && network.chainId != null ? Number(network.chainId) : null;
  } catch (_) {
    return null;
  }
};

// Returns the ethers v6 gas overrides for the chain `provider` is on (or the
// supplied knownChainId).
// - Polygon family (137, 80002): EIP-1559 fields clearing the 25 Gwei floor,
//   with maxFeePerGas = baseFee*2 + priorityFee (or a fixed ceiling when the
//   base fee can't be read).
// - Everything else (localhost/Hardhat 31337, unknown): {} — legacy gas, no
//   floor to clear.
// Never throws: a fee-read failure degrades to the fallback ceiling / static
// tip so a write is still sent rather than blocked.
export const getClientGasOverrides = async (provider, knownChainId = null) => {
  try {
    if (!provider) return {};

    const chainId = await resolveChainId(provider, knownChainId);
    if (!POLYGON_FAMILY_CHAIN_IDS.has(chainId)) {
      // Local Hardhat / unknown: legacy gas, no minimum-fee floor to clear.
      return {};
    }

    const maxPriorityFeePerGas = ethers.parseUnits(
      priorityFeeGwei().toString(),
      "gwei",
    );

    const baseFee = await readBaseFee(provider);
    const maxFeePerGas = baseFee
      ? baseFee * 2n + maxPriorityFeePerGas
      : ethers.parseUnits(fallbackMaxFeeGwei().toString(), "gwei");

    return { maxFeePerGas, maxPriorityFeePerGas };
  } catch (_) {
    // A helper that throws would block an otherwise-valid transaction. Fall
    // back to a static, floor-clearing tip/ceiling for the Polygon family, and
    // to no overrides when we can't even tell the chain.
    try {
      const chainId = Number.isFinite(knownChainId) ? Number(knownChainId) : null;
      if (!POLYGON_FAMILY_CHAIN_IDS.has(chainId)) return {};
      return {
        maxPriorityFeePerGas: ethers.parseUnits(
          priorityFeeGwei().toString(),
          "gwei",
        ),
        maxFeePerGas: ethers.parseUnits(fallbackMaxFeeGwei().toString(), "gwei"),
      };
    } catch (_e) {
      return {};
    }
  }
};
